import type { MetricKind, UnitSystem } from '../types'

const KG_TO_LB = 2.20462
const KM_TO_MI = 0.621371
const CM_TO_IN = 0.393701
const M_TO_YD = 1.09361

/** Normalizes a body weight to kilograms, the canonical unit for rules. */
export function toKg(value: number, unit: 'kg' | 'lb'): number {
  return unit === 'lb' ? value / KG_TO_LB : value
}

function clock(totalSeconds: number): string {
  const seconds = Math.max(0, Math.round(totalSeconds))
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const rest = seconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return hours > 0
    ? `${hours}:${pad(minutes)}:${pad(rest)}`
    : `${minutes}:${pad(rest)}`
}

/** Unit label for a metric under the active system. */
export function unitLabel(kind: MetricKind, system: UnitSystem): string {
  const imperial = system === 'imperial'
  switch (kind) {
    case 'weight':
      return imperial ? 'lbs' : 'kg'
    case 'height':
      return imperial ? 'in' : 'cm'
    case 'distance':
      return imperial ? 'mi' : 'km'
    case 'pace':
      return imperial ? '/mi' : '/km'
    case 'swimPace':
      return imperial ? '/100yd' : '/100m'
    case 'percent':
      return '%'
    case 'power':
      return 'W'
    case 'kcal':
      return 'kcal'
    case 'ratio':
      return 'W/kg'
    default:
      return ''
  }
}

/** Converts a metric-system value into the active system's number. */
export function convert(
  kind: MetricKind,
  value: number,
  system: UnitSystem
): number {
  if (system === 'metric') return value
  switch (kind) {
    case 'weight':
      return value * KG_TO_LB
    case 'height':
      return value * CM_TO_IN
    case 'distance':
      return value * KM_TO_MI
    case 'pace':
      return value / KM_TO_MI
    case 'swimPace':
      return value * M_TO_YD
    default:
      return value
  }
}

/** Display string without the unit suffix. */
export function formatValue(
  kind: MetricKind,
  value: number,
  system: UnitSystem
): string {
  const converted = convert(kind, value, system)
  switch (kind) {
    case 'pace':
    case 'swimPace':
    case 'duration':
      return clock(converted)
    case 'weight':
    case 'distance':
    case 'ratio':
      return converted.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    case 'percent':
      return converted.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    case 'bmi':
      return converted.toLocaleString('pt-BR', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })
    default:
      return String(Math.round(converted))
  }
}

/** Display string including the unit, when the kind has one. */
export function formatMetric(
  kind: MetricKind,
  value: number,
  system: UnitSystem
): string {
  const label = unitLabel(kind, system)
  const text = formatValue(kind, value, system)
  if (!label) return text
  if (kind === 'pace' || kind === 'swimPace') return `${text}${label}`
  return `${text} ${label}`
}

/**
 * Laboratory unit conversion (e.g. testosterone ng/dL ↔ nmol/L).
 * `factors` maps the canonical unit to each supported unit.
 */
export function convertLabValue(
  value: number,
  from: string,
  to: string,
  factors: Record<string, number>
): number {
  const fromFactor = factors[from] ?? 1
  const toFactor = factors[to] ?? 1
  return (value / fromFactor) * toFactor
}

export function formatLabValue(value: number, decimals: number, locale: string): string {
  return value.toLocaleString(locale, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
}

/** BMI is always derived: weight_kg / (height_m²). Never stored, never edited. */
export function calculateBmi(weightKg: number, heightCm: number): number {
  const meters = heightCm / 100
  if (meters <= 0) return 0
  return weightKg / (meters * meters)
}
